import { useInView } from "@/hooks/useInView";
import { Eye, Zap, FileCheck, MapPin } from "lucide-react";

const reasons = [
  {
    icon: Eye,
    badge: "Transparentność",
    title: "Mówimy wprost, jak wygląda proces",
    text: "Nie obiecujemy złotych gór z dnia na dzień. Od pierwszej rozmowy tłumaczymy realne terminy pozwoleń, zasady wypłaty czynszu i co dokładnie robimy w Twoim imieniu. Konkretną wycenę przedstawiamy na spotkaniu — po ocenie Twojej lokalizacji.",
    color: "emerald",
  },
  {
    icon: FileCheck,
    badge: "Pod klucz",
    title: "Całość formalności bierzemy na siebie",
    text: "Decyzje administracyjne, projekt budowlany, ekspertyzy środowiskowe, uzgodnienia z operatorami — prowadzimy każdy etap. Ty podpisujesz wyłącznie umowę dzierżawy, resztą zajmujemy się my.",
    color: "amber",
  },
  {
    icon: MapPin,
    badge: "Doświadczenie",
    title: "Pracujemy w całej Polsce",
    text: "Każdy proces administracyjny w Polsce ma swoje lokalne specyfiki — od miejscowości wiejskich po obrzeża aglomeracji. Znamy ścieżki urzędowe w 16 województwach.",
    color: "emerald",
  },
  {
    icon: Zap,
    badge: "Tempo",
    title: "Odpowiedź na zgłoszenie w 24 h",
    text: "Nie czekasz tygodniami na pierwszy kontakt. Po wypełnieniu formularza specjalista oddzwania w ciągu jednego dnia roboczego z pierwszą oceną potencjału Twojej lokalizacji.",
    color: "slate",
  },
];

export default function WhyUs() {
  const [ref, inView] = useInView(0.15);

  return (
    <section
      id="dlaczego-my"
      className="py-24 bg-[#FAFAFA]"
      data-testid="why-us-section"
    >
      <div className="max-w-7xl mx-auto px-6">
        <div
          ref={ref}
          className={`text-center mb-14 transition-all duration-700 ${
            inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-emerald-700 text-sm font-semibold uppercase tracking-widest">
            Dlaczego my?
          </span>
          <h2 className="text-4xl sm:text-5xl font-black text-slate-900 mt-2 tracking-tight">
            Cztery powody, dla których właściciele wybierają Telecom BTS
          </h2>
          <p className="text-slate-600 text-lg mt-4 max-w-2xl mx-auto">
            W branży telekomunikacyjnej działa wiele firm. Oto czym wyróżniamy się od konkurencji.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {reasons.map((r, i) => (
            <ReasonCard key={r.title} reason={r} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function ReasonCard({ reason, index }) {
  const [ref, inView] = useInView();
  const Icon = reason.icon;

  const colorMap = {
    emerald: { bg: "bg-emerald-100", text: "text-emerald-700", border: "hover:border-emerald-300" },
    amber: { bg: "bg-amber-100", text: "text-amber-700", border: "hover:border-amber-300" },
    slate: { bg: "bg-slate-100", text: "text-slate-700", border: "hover:border-slate-400" },
  };
  const c = colorMap[reason.color];

  return (
    <div
      ref={ref}
      className={`bg-white rounded-2xl p-6 border border-slate-200 ${c.border} hover:shadow-lg hover:-translate-y-1 transition-all duration-700 flex flex-col h-full ${
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: `${index * 100}ms` }}
      data-testid={`reason-card-${index + 1}`}
    >
      <div className={`w-11 h-11 ${c.bg} rounded-xl flex items-center justify-center mb-4`}>
        <Icon className={`w-5 h-5 ${c.text}`} aria-hidden="true" />
      </div>
      <span className={`text-[11px] font-bold uppercase tracking-widest ${c.text} mb-2`}>
        {reason.badge}
      </span>
      <h3 className="font-bold text-slate-900 text-base leading-tight mb-2.5">
        {reason.title}
      </h3>
      <p className="text-slate-600 text-sm leading-relaxed">
        {reason.text}
      </p>
    </div>
  );
}
